import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 py-16" data-testid="reset-page">
      <div className="spotlight-card w-full max-w-md p-10">
        <div className="mb-8 flex justify-center"><Logo /></div>
        {done ? (
          <div className="text-center" data-testid="reset-success-panel">
            <CheckCircle2 size={40} className="mx-auto text-gold" />
            <h1 className="mt-5 font-serif text-2xl text-slate-100">Password Updated</h1>
            <p className="mt-3 text-sm text-zinc-400">You can now sign in with your new password.</p>
            <Link to="/account" data-testid="reset-signin-link"
              className="mt-8 inline-block rounded-full bg-gold px-8 py-3 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase">
              Sign In
            </Link>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid-panel">
            <h1 className="font-serif text-2xl text-slate-100">Invalid Link</h1>
            <p className="mt-3 text-sm text-zinc-400">This reset link is missing or malformed. Request a new one.</p>
            <Link to="/account" data-testid="reset-request-new-link"
              className="mt-8 inline-block rounded-full border border-gold/40 px-8 py-3 font-display text-xs font-semibold tracking-[0.25em] text-gold uppercase">
              Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <h1 className="text-center font-serif text-2xl text-slate-100">Choose a New Password</h1>
            <div>
              <label className="lux-label" htmlFor="reset-password">New Password</label>
              <input id="reset-password" data-testid="reset-password-input" type="password" required minLength={8}
                className="lux-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8+ characters" />
            </div>
            <div>
              <label className="lux-label" htmlFor="reset-confirm">Confirm Password</label>
              <input id="reset-confirm" data-testid="reset-confirm-input" type="password" required minLength={8}
                className="lux-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="reset-submit-btn" disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright disabled:opacity-60">
              {busy && <Loader2 size={15} className="animate-spin" />}
              Update Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
